<template>
    <div>
        <HamletTable
            ref="hamletTable"
            :columns="fields"
            :fetch="fetch"
            :search="true"
            table-container-class="table-responsive hamlet-table review-processes-table"
            :defaultPerPage="savedPerPage"
            :page-options="perPageOptions"
            :default-filters="filters"
            @onRowClick="onRowClick"
            @per-page-change="onPerPageChange"
            @page-change="onPageChange"
        >
            <template #header-label>
                {{ $t('Component(ReviewProcess):processes-grid-label') }}
            </template>

            <template #cell-status="data">
                <b-badge :variant="statusVariant(data.item.status)">
                    {{ translateProcessStatus(data.item.status) }}
                </b-badge>
            </template>

            <template #cell-reviewableType="data">
                {{ translateReviewableType(data.item.reviewableType) }}
            </template>

            <template #cell-itemsCount="data">
                {{ data.item.itemsCount !== null && data.item.itemsCount !== undefined ? data.item.itemsCount : '-' }}
            </template>

            <template #cell-createdAt="data">
                {{ formatDate(data.item.createdAt) }}
            </template>

            <template #cell-updatedAt="data">
                {{ formatDate(data.item.updatedAt) }}
            </template>

            <template #filter-cell-status>
                <VueSelectFilter
                    style="min-width: 170px;"
                    :options="statusOptions"
                    :initial-value="selectedStatusOptions"
                    :append-to-body="true"
                    @filter="filterByStatus"
                />
            </template>

            <template #filter-cell-reviewableType>
                <VueSelectFilter
                    style="min-width: 170px;"
                    :options="reviewableTypeOptions"
                    :initial-value="selectedReviewableTypeOptions"
                    :append-to-body="true"
                    @filter="filterByReviewableType"
                />
            </template>
        </HamletTable>
    </div>
</template>

<script>
import {BBadge} from 'bootstrap-vue'
import {HamletTable} from '@core/components/common'
import {VueSelectFilter} from '@core/components/common/filter'
import {ResearchReviewApi} from '@core/api'
import backendResponse from '@core/mixins/ui/backend-response'
import dateRenderer from '@core/mixins/date-renderer'

const DEFAULT_PER_PAGE = 20

function normalizeSettings(settings) {
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return {
            filters: {},
            perPage: DEFAULT_PER_PAGE,
            page: 1,
        }
    }

    return {
        filters: structuredClone(settings.filters || {}),
        perPage: Number(settings.perPage) || DEFAULT_PER_PAGE,
        page: Number(settings.page) || 1,
    }
}

export default {
    name: 'ResearchReviewGrid',
    components: {
        BBadge,
        HamletTable,
        VueSelectFilter,
    },
    mixins: [backendResponse, dateRenderer],
    data() {
        const savedSettings = normalizeSettings(this.$store.getters.runtimeConfig.researchReviewRequestsSettings)

        return {
            filters: savedSettings.filters,
            savedPerPage: savedSettings.perPage,
            savedPage: savedSettings.page,
            fields: [
                {key: 'status', label: this.$t('Component(Grid):column-status')},
                {key: 'reviewableType', label: this.$t('Component(Grid):column-type')},
                {key: 'processTitle', label: this.$t('Component(Grid):column-title')},
                {key: 'itemsCount', label: this.$t('Component(Grid):column-count')},
                {key: 'createdAt', label: this.$t('Component(Label):created-at')},
                {key: 'updatedAt', label: this.$t('Component(Label):updated-at')},
            ],
            perPageOptions: [10, 20, 30, 50],
        }
    },
    computed: {
        statusOptions() {
            return this.$store.getters['researchReview/processStatuses']
                .map(status => ({
                    status,
                    title: this.$t('Component(ResearchReviewProcessEnum):status-' + status),
                }))
        },
        reviewableTypeOptions() {
            return this.$store.getters['researchReview/reviewableTypes']
                .map(reviewableType => ({
                    reviewableType,
                    title: this.$t('Component(ResearchReviewProcessEnum):reviewable-type-' + reviewableType),
                }))
        },
        selectedStatusOptions() {
            if (!this.filters.status) {
                return []
            }

            return this.statusOptions.filter(option => option.status === this.filters.status)
        },
        selectedReviewableTypeOptions() {
            if (!this.filters.reviewableType) {
                return []
            }

            return this.reviewableTypeOptions.filter(option => option.reviewableType === this.filters.reviewableType)
        },
    },
    methods: {
        formatDate(value) {
            if (!value) {
                return '-'
            }

            return this.renderDateTime(value)
        },
        translateProcessStatus(status) {
            if (!status) {
                return '-'
            }

            return this.$t('Component(ResearchReviewProcessEnum):status-' + status)
        },
        translateReviewableType(reviewableType) {
            if (!reviewableType) {
                return '-'
            }

            return this.$t('Component(ResearchReviewProcessEnum):reviewable-type-' + reviewableType)
        },
        statusVariant(status) {
            const map = {
                pending_machine_review: 'warning',
                pending_human_review: 'info',
                completed: 'success',
            }

            return map[status] || 'secondary'
        },
        onPerPageChange(perPage) {
            this.savedPerPage = perPage
            this.saveSettings()
        },
        onPageChange(page) {
            this.savedPage = page
            this.saveSettings()
        },
        saveSettings() {
            this.$store.dispatch('setRuntimeProperty', {
                name: 'researchReviewRequestsSettings',
                value: {
                    filters: this.filters,
                    perPage: this.savedPerPage,
                    page: this.savedPage,
                },
                removeOnOrganizationSwitch: true,
            })
        },
        setFilterValue(key, value) {
            if (value === null || value === undefined || value === '') {
                if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
                    delete this.filters[key]
                }
                return
            }

            this.filters[key] = value
        },
        filterByStatus(items) {
            const status = items.length > 0 ? items[0].status : null
            this.setFilterValue('status', status)
            this.$refs.hamletTable.filter('status', status)
            this.saveSettings()
        },
        filterByReviewableType(items) {
            const reviewableType = items.length > 0 ? items[0].reviewableType : null
            this.setFilterValue('reviewableType', reviewableType)
            this.$refs.hamletTable.filter('reviewableType', reviewableType)
            this.saveSettings()
        },
        onRowClick(item) {
            this.$router.push({name: 'research-review-card', params: {id: item.processId}})
        },
        fetch(config, cb) {
            this.savedPage = config.page
            this.savedPerPage = config.perPage
            this.saveSettings()

            return ResearchReviewApi
                .getProcesses(config.filters, config.page, config.perPage)
                .then((response) => {
                    cb(response.data.total, response.data.items)
                })
                .catch((e) => {
                    cb(0, [])
                    this.handleBackendError(e)
                })
        },
    },
}
</script>

<style>
.review-processes-table table th,
.review-processes-table table td {
    padding: 0.72rem 1rem;
}
</style>
